'use client';
import { LeagueEvent } from '@interfaces/APITravel';
import { v2Links } from '@utils/navigation/links';
import { CurrencyDropdown } from '@v2/containers/CurrencyPicker/CurrencyDropdown';
import { Button } from '@v2/ui/Button';
import { Close } from '@v2/ui/Icon/General';
import { ShoppingCart01 } from '@v2/ui/Icon/ShoppingCart01/ShoppingCart01';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@v2/ui/Dialog';
import { Logo } from '@v2/ui/Logo';
import { Popover, PopoverContent, PopoverTrigger } from '@v2/ui/Popover';
import { typographyVariants } from '@v2/ui/Typography';
import { cn } from '@v2/utils/cn';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useFlightSearchStore } from '@store/flight-search-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { useTicketsStore } from '@store/tickets-store';
import invalidatePackageData from '@utils/cache-invalidate';
import { formatEventDate, getInternationalTime } from '@utils/dates';
import HotelMapFilterDialog from '@v2/views/SearchHotel/components/HotelContent/HotelMapFilterDialog';
import { BuildPackageSteps } from './BuildPackageSteps';
import { CheckoutHeaderQuote } from './checkout-header-quote';
import { formatEventSubtitle } from './header-display';
import { NeedHelpButton } from './NeedHelpButton';
import { PackageSummary } from './PackageSummary';

type Props = {
  event: LeagueEvent;
  quote?: CheckoutHeaderQuote | null;
};

export function BuyPackageHeader({ event, quote }: Props) {
  const isMapFullScreen = useSearchHotelStore((state) => state.isMapFullScreen);

  if (!event) return null;

  // Venue wall-clock time (occurs_at_local carries the venue's offset) — never convert to ET.
  const date = formatEventDate(event.occurs_at_local, 'MMM D, YYYY');
  const time = getInternationalTime(event.occurs_at_local, true);

  const venueName = event.venue?.name;
  const venueLocation = event.venue?.location;
  const compactSubtitle = formatEventSubtitle({
    date,
    time,
    venueName,
    venueLocation,
    includeLocation: false,
  });
  const fullSubtitle = formatEventSubtitle({
    date,
    time,
    venueName,
    venueLocation,
    includeLocation: true,
  });

  return (
    <header className="relative z-[30] border-b border-gray-200 bg-white">
      <CompactBuildPackageHeader
        eventName={event.name}
        subtitle={compactSubtitle}
        isMapFullScreen={isMapFullScreen}
        quote={quote}
      />
      <DesktopBuildPackageHeader
        eventName={event.name}
        subtitle={fullSubtitle}
        isMapFullScreen={isMapFullScreen}
        quote={quote}
      />
    </header>
  );
}

function CompactBuildPackageHeader({
  eventName,
  subtitle,
  isMapFullScreen,
  quote,
}: {
  eventName: string;
  subtitle: string;
  isMapFullScreen: boolean;
  quote?: CheckoutHeaderQuote | null;
}) {
  return (
    <div className="flex w-full flex-col gap-3 px-4 py-3 lg:hidden">
      <div className="grid grid-cols-[40px_minmax(0,1fr)_40px] items-center gap-2">
        <div className="flex size-10 shrink-0 items-center justify-center">
          <Logo
            variant="black"
            width={34}
            height={32}
            size={34}
            className="h-8 w-[34px] object-contain"
          />
        </div>
        <div className="min-w-0 text-center">
          <p className="line-clamp-2 break-words text-xs font-semibold leading-4 text-gray-800 min-[600px]:line-clamp-none min-[600px]:truncate min-[600px]:text-base min-[600px]:leading-6">
            {eventName}
          </p>
          <p
            className="mt-0.5 line-clamp-2 break-words text-[10px] font-normal leading-[14px] text-gray-500 min-[600px]:line-clamp-none min-[600px]:truncate min-[600px]:text-xs min-[600px]:leading-[18px]"
            suppressHydrationWarning
          >
            {subtitle}
          </p>
        </div>
        {isMapFullScreen ? <CloseMapButton /> : <LeavePageModal />}
      </div>

      {/* Equal 1fr sides center the steps; the actions column never shrinks below its buttons. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 min-[600px]:items-center">
        <div className="col-start-2 flex min-w-0 justify-center">
          <BuildPackageSteps />
        </div>
        <div className="col-start-3 flex justify-end">
          {isMapFullScreen ? (
            <HotelMapFilterDialog />
          ) : (
            <div className="flex shrink-0 items-center gap-4">
              <PackageCartButton quote={quote} />
              <NeedHelpButton compact />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PackageCartButton({ quote }: { quote?: CheckoutHeaderQuote | null }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Package total"
          className="hidden size-10 shrink-0 items-center justify-center rounded-lg text-gray-800 min-[600px]:inline-flex"
        >
          <ShoppingCart01 size="20" className="size-5 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-3">
        <PackageSummary quote={quote} showWhenEmpty showChevron={false} />
      </PopoverContent>
    </Popover>
  );
}

function DesktopBuildPackageHeader({
  eventName,
  subtitle,
  isMapFullScreen,
  quote,
}: {
  eventName: string;
  subtitle: string;
  isMapFullScreen: boolean;
  quote?: CheckoutHeaderQuote | null;
}) {
  return (
    <div className="hidden w-full items-center justify-between gap-4 px-6 py-4 lg:flex">
      <div className="flex min-w-0 max-w-[40%] flex-1 items-center gap-4">
        {isMapFullScreen ? (
          <CloseMapButton large />
        ) : (
          <Logo
            variant="black"
            width={34}
            height={32}
            size={34}
            className="h-8 w-[34px] shrink-0 object-contain"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold leading-6 text-gray-800">
            {eventName}
          </p>
          <p
            className="truncate text-sm font-medium leading-5 text-gray-500"
            suppressHydrationWarning
          >
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex flex-1 justify-center">
        <BuildPackageSteps />
      </div>

      <div className="flex flex-1 shrink-0 items-center justify-end gap-4">
        {isMapFullScreen ? (
          <HotelMapFilterDialog />
        ) : (
          <>
            <PackageSummary quote={quote} />
            <CurrencyDropdown
              variant="black"
              className="mr-0 justify-center gap-2 px-2 py-2 xl:w-[100px] xl:px-3.5 [&>h4]:hidden xl:[&>h4]:inline xl:[&>h4]:text-sm"
            />
            <NeedHelpButton />
            <LeavePageModal />
          </>
        )}
      </div>
    </div>
  );
}

function CloseMapButton({ large = false }: { large?: boolean }) {
  return (
    <Button
      className={cn('shrink-0 rounded-lg p-0', large ? 'size-12' : 'size-10')}
      variant="secondary"
      aria-label="Close map"
      onClick={() => {
        useSearchHotelStore.setState({ isMapFullScreen: false });
      }}
    >
      <Close className="text-gray-800" size="20" />
    </Button>
  );
}

function LeavePageModal() {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          aria-label="Leave page"
          variant="secondary"
          size="sm"
          className="size-10 shrink-0 rounded-lg bg-white p-2.5 lg:size-12 lg:p-3.5"
        >
          <Close pathProps={{ stroke: '#1D2939' }} size="20" />
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-10">
        <DialogHeader>
          <DialogTitle
            className={cn(
              typographyVariants({ size: 'dsm' }),
              'text-center font-bold'
            )}
          >
            Leave page?
          </DialogTitle>
        </DialogHeader>

        <DialogFooter className="justify-star md:gap-y0 w-full gap-y-3">
          <Button
            className="w-full"
            variant="secondary"
            onClick={() => {
              invalidatePackageData();
              setOpen(false);
              router.push(v2Links.home);
              useTicketsStore.getState().reset();
              useSearchHotelStore.getState().reset();
              useCheckoutStepsStore.getState().clearAllState();
              useFlightSearchStore.getState().clearSelectedFlights();
            }}
          >
            Leave without saving
          </Button>
          <Button className="w-full" onClick={() => setOpen(false)}>
            Stay here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
